import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Tooltip,
} from '@mui/material';
import { useQuery } from 'react-query';
import { capitalize } from 'lodash';

import { MainLayout } from '../components/layout/MainLayout';
import { queueApi } from '../services/api';
import { useToast } from '../components/common/ToastProvider';
import { QueueJob } from '../services/interfaces';

const formatDate = (timestamp?: number): string => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString();
};

const StatusCard = ({
  title,
  value,
  color,
}: {
  title: string;
  value: number;
  color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';
}) => (
  <Card
    variant='outlined'
    sx={{ flex: 1 }}
  >
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography
        variant='h4'
        color={`${color}.main`}
        gutterBottom
      >
        {value}
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
      >
        {title}
      </Typography>
    </CardContent>
  </Card>
);

const JobsTable = ({
  title,
  jobs,
  emptyMessage,
}: {
  title: string;
  jobs: QueueJob[];
  emptyMessage: string;
}) => {
  if (jobs.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant='subtitle1'
          gutterBottom
        >
          {title}
        </Typography>
        <Alert severity='info'>{emptyMessage}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant='subtitle1'
        gutterBottom
      >
        {title} ({jobs.length})
      </Typography>
      <TableContainer
        component={Paper}
        variant='outlined'
      >
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell>Job Name</TableCell>
              <TableCell>Call ID</TableCell>
              <TableCell>Campaign ID</TableCell>
              <TableCell>Attempts</TableCell>
              <TableCell>Return Value</TableCell>
              <TableCell>Processed On</TableCell>
              <TableCell>Finished On</TableCell>
              <TableCell>Failed Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                key={job.id}
                hover
              >
                <TableCell>{capitalize(job.name)}</TableCell>
                <TableCell>
                  <Typography
                    variant='body2'
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {job.data.callId.slice(0, 8)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant='body2'
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {job.data.campaignId.slice(0, 8)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${job.attemptsMade} / ${job.opts.attempts}`}
                    size='small'
                    color={
                      job.attemptsMade >= job.opts.attempts
                        ? 'error'
                        : 'default'
                    }
                  />
                </TableCell>
                <TableCell>
                  {job.returnvalue ? String(job.returnvalue) : 'N/A'}
                </TableCell>
                <TableCell>{formatDate(job.processedOn)}</TableCell>
                <TableCell>{formatDate(job.finishedOn)}</TableCell>
                <TableCell>
                  {job.failedReason ? (
                    <Tooltip title={job.failedReason}>
                      <Typography
                        variant='body2'
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'error.main',
                        }}
                      >
                        {job.failedReason}
                      </Typography>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const QueuePage = () => {
  const { showToast } = useToast();

  const getStatusQuery = useQuery(
    ['queueApi.getStatus'],
    () => queueApi.getStatus(),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to load queue status', 'error');
      },
      refetchInterval: 5000,
    }
  );

  const getJobsQuery = useQuery(
    ['queueApi.getJobs'],
    () => queueApi.getJobs(),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to load queue jobs', 'error');
      },
      refetchInterval: 5000,
    }
  );

  const renderStatusContent = () => {
    if (getStatusQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (getStatusQuery.isError || !getStatusQuery.data) {
      return <Alert severity='error'>Failed to load queue status</Alert>;
    }

    const { waiting, active, failed } = getStatusQuery.data;

    return (
      <Grid
        container
        spacing={2}
      >
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatusCard
            title='Waiting'
            value={waiting}
            color='default'
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatusCard
            title='Active'
            value={active}
            color='primary'
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatusCard
            title='Failed'
            value={failed}
            color='error'
          />
        </Grid>
      </Grid>
    );
  };

  const renderJobsContent = () => {
    if (getJobsQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (getJobsQuery.isError || !getJobsQuery.data) {
      return <Alert severity='error'>Failed to load queue jobs</Alert>;
    }

    const { active, failed, waiting } = getJobsQuery.data;

    return (
      <Stack spacing={3}>
        <JobsTable
          title='Active Jobs'
          jobs={active}
          emptyMessage='No active jobs'
        />
        <JobsTable
          title='Waiting Jobs'
          jobs={waiting}
          emptyMessage='No waiting jobs'
        />
        <JobsTable
          title='Failed Jobs'
          jobs={failed}
          emptyMessage='No failed jobs'
        />
      </Stack>
    );
  };

  return (
    <MainLayout>
      <Stack
        spacing={4}
        width='100%'
      >
        <Typography
          variant='h4'
          gutterBottom
        >
          Queue
        </Typography>

        <Card>
          <CardHeader title='Queue Status' />
          <CardContent>{renderStatusContent()}</CardContent>
        </Card>

        <Card>
          <CardHeader title='Queue Jobs' />
          <CardContent>{renderJobsContent()}</CardContent>
        </Card>
      </Stack>
    </MainLayout>
  );
};
